/**
 * HistoryService
 * Retrieves classification history from the backend with caching
 * 
 * Requirements: 8.1, 8.4
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { AuthService } from './AuthService';
import config from '../config';
import type { ClassificationResult } from '../types';

interface HistoryCache {
  data: ClassificationResult[];
  timestamp: number;
  limit: number;
  offset: number;
}

export class HistoryService {
  private axiosInstance: AxiosInstance;
  private authService: AuthService;
  private cache: Map<string, HistoryCache>;
  private readonly cacheTTL: number = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.authService = AuthService.getInstance();
    this.cache = new Map();

    // Create axios instance with base configuration
    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 10000, // 10 second timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include JWT token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          const token = await this.authService.getValidAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Failed to get access token:', error);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          console.error('Authentication failed');
          await this.authService.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get classification history with pagination
   * Requirements: 8.1, 8.4
   * 
   * @param limit - Maximum number of items to return
   * @param offset - Number of items to skip
   * @returns Promise resolving to array of classification results
   * @throws Error if fetch fails
   */
  async getHistory(limit: number = 20, offset: number = 0): Promise<ClassificationResult[]> {
    // Generate cache key
    const cacheKey = `${limit}-${offset}`;

    // Check cache
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
      console.log('Returning cached history:', cacheKey);
      return cached.data;
    }

    try {
      const response = await this.axiosInstance.get<{ history: ClassificationResult[] }>(
        '/api/history',
        {
          params: {
            limit,
            offset,
          },
        }
      );

      const history = response.data.history;

      // Update cache
      this.cache.set(cacheKey, {
        data: history,
        timestamp: Date.now(),
        limit,
        offset,
      });

      console.log(`Fetched history: ${history.length} items (limit: ${limit}, offset: ${offset})`);
      return history;
    } catch (error) {
      console.error('Failed to fetch history:', error);
      throw this.handleError(error, 'Failed to fetch classification history');
    }
  }

  /**
   * Get single classification by frame_id
   * 
   * @param frameId - The frame ID to retrieve
   * @returns Promise resolving to classification result
   * @throws Error if fetch fails
   */
  async getClassification(frameId: string): Promise<ClassificationResult> {
    try {
      const response = await this.axiosInstance.get<ClassificationResult>(
        `/api/history/${frameId}`
      );

      console.log('Fetched classification:', frameId);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch classification:', error);
      throw this.handleError(error, 'Failed to fetch classification details');
    }
  }

  /**
   * Delete a specific classification result
   * 
   * @param frameId - The frame ID to delete
   * @returns Promise resolving to success message
   * @throws Error if deletion fails
   */
  async deleteHistoryItem(frameId: string): Promise<string> {
    try {
      const response = await this.axiosInstance.delete<{ message: string }>(
        `/api/history/${frameId}`
      );

      // Clear cache since data has changed
      this.clearCache();

      console.log('Deleted history item:', frameId);
      return response.data.message;
    } catch (error) {
      console.error('Failed to delete history item:', error);
      throw this.handleError(error, 'Failed to delete classification');
    }
  }

  /**
   * Save user feedback on a specific classification result
   *
   * @param frameId - The frame ID to update
   * @param feedback - Feedback text (max 1000 chars)
   * @returns Promise resolving when feedback is saved
   * @throws Error if saving fails
   */
  async saveFeedback(frameId: string, feedback: string): Promise<void> {
    try {
      await this.axiosInstance.patch(
        `/api/history/${encodeURIComponent(frameId)}/feedback`,
        { feedback }
      );

      // Invalidate cache so next history fetch returns updated data
      this.clearCache();

      console.log('Saved feedback for frame:', frameId);
    } catch (error) {
      console.error('Failed to save feedback:', error);
      throw this.handleError(error, 'Failed to save feedback');
    }
  }

  /**
   * Save a thumbs-up / thumbs-down vote on a specific criterion result
   *
   * @param frameId - The frame ID of the result to update
   * @param criterionName - Name of the criterion being voted on
   * @param vote - 'up' (good classification) or 'down' (bad classification)
   * @returns Promise resolving when the vote is saved
   * @throws Error if saving fails
   */
  async saveCriterionVote(
    frameId: string,
    criterionName: string,
    vote: 'up' | 'down'
  ): Promise<void> {
    try {
      await this.axiosInstance.patch(
        `/api/history/${encodeURIComponent(frameId)}/criterion-vote`,
        { criterion_name: criterionName, vote }
      );
      this.clearCache();
      console.log(`Saved criterion vote for frame ${frameId}, criterion '${criterionName}': ${vote}`);
    } catch (error) {
      console.error('Failed to save criterion vote:', error);
      throw this.handleError(error, 'Failed to save criterion vote');
    }
  }

  /**
   * Delete all classification history for the user
   *
   * @returns Promise resolving to success message with count
   * @throws Error if deletion fails
   */
  async deleteAllHistory(): Promise<string> {
    try {
      const response = await this.axiosInstance.delete<{ message: string }>(
        '/api/history'
      );

      // Clear cache since all data has been deleted
      this.clearCache();

      console.log('Deleted all history');
      return response.data.message;
    } catch (error) {
      console.error('Failed to delete all history:', error);
      throw this.handleError(error, 'Failed to delete all classifications');
    }
  }

  /**
   * Clear cache
   * Useful when new classifications are added or user wants fresh data
   */
  clearCache(): void {
    this.cache.clear();
    console.log('History cache cleared');
  }

  /**
   * Clear specific cache entry
   * 
   * @param limit - The limit used in the original request
   * @param offset - The offset used in the original request
   */
  clearCacheEntry(limit: number, offset: number): void {
    const cacheKey = `${limit}-${offset}`;
    this.cache.delete(cacheKey);
    console.log('Cleared cache entry:', cacheKey);
  }

  /**
   * Get cache statistics
   * 
   * @returns Object with cache size and entries
   */
  getCacheStats(): { size: number; entries: string[] } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys()),
    };
  }

  /**
   * Handle errors and convert to user-friendly messages
   * 
   * @param error - The error to handle
   * @param defaultMessage - Default message if error cannot be parsed
   * @returns Error with user-friendly message
   */
  private handleError(error: unknown, defaultMessage: string): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      if (axiosError.code === 'ECONNABORTED') {
        return new Error('Request timeout. Please check your connection.');
      }

      if (axiosError.code === 'ERR_NETWORK') {
        return new Error('Network error. Please check your connection.');
      }

      if (axiosError.response) {
        const status = axiosError.response.status;
        const data = axiosError.response.data as any;

        switch (status) {
          case 400:
            // Try to extract error message from response
            if (data?.detail) {
              return new Error(data.detail);
            }
            return new Error('Invalid request parameters.');
          case 401:
            return new Error('Authentication failed. Please log in again.');
          case 403:
            return new Error('You do not have permission to view this history.');
          case 404:
            return new Error('Classification not found.');
          case 429:
            return new Error('Too many requests. Please wait a moment.');
          case 500:
          case 502:
          case 503:
            return new Error('Server error. Please try again later.');
          default:
            if (data?.detail) {
              return new Error(data.detail);
            }
            return new Error(`${defaultMessage} (Status: ${status})`);
        }
      }

      return new Error('Network error. Please check your connection.');
    }

    if (error instanceof Error) {
      return error;
    }

    return new Error(defaultMessage);
  }
}

export default HistoryService;
